import {clsx} from 'clsx';
import _ from 'lodash';
import memoize from 'memoize-one';
import React, {
  Component,
  createRef,
  type CSSProperties,
  type RefObject,
  useCallback,
} from 'react';
import {useInView} from 'react-intersection-observer';

import {Analytics} from '../../../services/analytics';
import {useViewer} from '../../../state/viewer/hooks';
import {TABLE_ROW_HEIGHT} from '../../../util/constants';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import type * as FilterTypes from '../../../util/filterTypes';
import {addFilterFromWBTableToStored} from '../../../util/filterV2';
import type {Sort as QuerySort} from '../../../util/queryTypes';
import {
  type Config as TableSettings,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../../util/runfeed';
import type * as RunTypes from '../../../util/runTypes';
import * as TableCols from '../../../util/tablecols';
import {getColumnWidth} from '../../../util/wbtree';
import {useRunSelectorResizingContext} from '../../MultiRunWorkspace/RunSelectorResizingContext';
import {useWorkspaceDrawerContextMaybe} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {
  getPageRows,
  isPastLastPage,
  type RunsPagingMode,
} from '../hooks/useRunsPaging';
import {RunsCounter} from '../RunsCounter';
import type {
  RowForWBTableRow,
  WBTableColumn,
  WBTableGridContainerProps,
  WBTableProps,
} from './types';
import {WBTableActions} from './WBTableActions';
import {WBTableGridContainer} from './WBTableGridContainer';
import {
  WBTablePagination,
  WBTablePaginationContainer,
} from './WBTablePagination';
import {WBTableTitleActions} from './WBTableTitleActions';

const RUNS_SELECTOR_BORDER_WIDTH = 1;

interface WBTableState {
  columnDragAccessor?: string;
  columnDropAccessor?: string;
  currentPage: number;
  scrolledHorizontally: boolean;
  scrolledVertically: boolean;
  scrollXBounds: [number, number];
  childrenPaginationMap: {[key: string]: number | undefined}; // maps row.__address__ to the active page for each paginated group, like {groupId2 => 2, groupId2 => 5}
  expandedRowAddresses: string[];

  // expandability
  currentClientX: number;
  dragInitialValues: {
    clientX: number;
    width: number;
    runNameColumnWidth: number;
    maxPinnedColumnsWidth: number;
  };
  dragCurrentValues: {
    runNameColumnWidth: number;
    clientX: number;
  } | null;
  columnResizeOffset: number;
  viewportWidth: number;
  viewportHeight: number;
}

type AllWBTableProps = WBTableProps & ReturnType<typeof useWBTableProps>;
export class WBTableComp extends Component<AllWBTableProps, WBTableState> {
  static getDerivedStateFromProps(
    props: AllWBTableProps,
    state: WBTableState
  ): null | Partial<WBTableState> {
    if (props.loading && _.isEmpty(props.rows)) {
      return null;
    }

    const expandedRowAddresses =
      props.expandedRowAddresses || state.expandedRowAddresses || [];
    return {
      expandedRowAddresses,
    };
  }

  scrollRef = createRef<HTMLDivElement>();

  pinnedTreeRef = createRef<HTMLDivElement>();
  unpinnedTreeRef = createRef<HTMLDivElement>();

  containerRef = createRef<HTMLDivElement>();
  wrapperRef = createRef<HTMLDivElement | null>();

  currentWidth = 0;
  snapToWidths: number[] = [];

  visibleColumnsInternal = memoize(
    (tableSettings: TableSettings, columns: WBTableColumn[]) => {
      return TableCols.getOrderedColumns(tableSettings, columns);
    }
  );

  tableSettingsInternal = memoize(
    (tableSettings: TableSettings, columns: WBTableColumn[]) => {
      return TableCols.initializeTableSettings(tableSettings, columns);
    }
  );

  rowsInternal = memoize(
    (
      pageSize: number,
      currentPage: number,
      rows: Array<RowForWBTableRow>,
      pagingMode: RunsPagingMode
    ) => {
      const allRows = rows || [];
      // if rows don't have unique ids, add them
      if (allRows[0] && _.isUndefined(allRows[0].__address__)) {
        allRows.forEach((r, i) => (r.__address__ = i + ''));
      }
      return getPageRows({
        currentPage,
        pageSize,
        pagingMode,
        rows: allRows,
      });
    }
  );

  // duplicated from WBTableGridContainer; consider de-duping
  pinnedColumns = memoize(
    (tableSettings: TableSettings, columns: WBTableColumn[]) => {
      return columns.filter(c => TableCols.isPinned(tableSettings, c.accessor));
    }
  );

  updateScrollXBounds = _.throttle(
    () => {
      const node = this.scrollRef.current;
      if (node == null) {
        return;
      }

      const scrollXBounds: [number, number] = [
        node.scrollLeft,
        node.scrollLeft + node.offsetWidth,
      ];
      if (!_.isEqual(scrollXBounds, this.state.scrollXBounds)) {
        this.setState({scrollXBounds});
      }
    },
    300,
    {leading: false}
  );

  handleResize = _.throttle(() => {
    this.setState({
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
  }, 200);

  constructor(props: AllWBTableProps) {
    super(props);
    this.state = {
      columnDragAccessor: undefined,
      columnDropAccessor: undefined,
      currentPage: 1,
      scrolledHorizontally: false,
      scrolledVertically: false,
      scrollXBounds: [0, 4000],
      childrenPaginationMap: {},
      expandedRowAddresses: [],

      // expandability
      currentClientX: 0,
      dragInitialValues: {
        clientX: 0,
        width: 0,
        runNameColumnWidth: 0,
        maxPinnedColumnsWidth: 0,
      },
      dragCurrentValues: null,
      columnResizeOffset: 0,
      viewportWidth: 0,
      viewportHeight: 0,
    };
  }

  maxPage = () => {
    return Math.ceil(
      (this.props.totalRowCount || this.props.rows.length) / this.pageSize()
    );
  };

  pageSize = () => {
    return this.tableSettings().pageSize || EMPTY_TABLE_SETTINGS.pageSize;
  };

  tableSettings = () => {
    const settings = this.tableSettingsInternal(
      this.props.tableSettings || EMPTY_TABLE_SETTINGS,
      this.props.columns
    );

    return {
      ...settings,
      columnWidths: {
        ...settings.columnWidths,
        // See handleMouseMove for why we do things this way. This is purely a
        // performance workaround for large workspaces.
        // The resizing flag lives in context and can outlive this instance
        // (e.g. a paging mode change remounts the table mid-drag), so
        // dragCurrentValues may still be null here.
        'run:displayName':
          (this.props.isResizingFullHeight
            ? this.state.dragCurrentValues?.runNameColumnWidth
            : undefined) ?? settings.columnWidths['run:displayName'],
      },
    };
  };

  grouping = () => {
    return this.props.grouping || [];
  };

  rows = () => {
    return this.rowsInternal(
      this.pageSize(),
      this.state.currentPage,
      this.props.rows,
      this.props.runsPagingMode
    );
  };

  resetPagination = () => {
    if (this.props.clearTableMessage) {
      this.props.clearTableMessage();
    }
    this.setCurrentPage(1);
  };

  setCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
    this.props.onSetCurrentPage?.(currentPage);
  };

  // sets the pagination (active page) for the given rowAddress
  setChildrenPagination = (rowAddress: string, activePage?: number) => {
    return this.setState({
      childrenPaginationMap: {
        ...this.state.childrenPaginationMap,
        [rowAddress]: activePage,
      },
    });
  };

  // open or close the row at the given address (rowAddress looks like '1-6-5-1-5')
  toggleExpandedRow = (rowAddress: string) => {
    if (this.props.toggleExpandedRow) {
      this.props.toggleExpandedRow(rowAddress);
    } else {
      const {expandedRowAddresses} = this.state;
      this.setState({
        expandedRowAddresses: _.includes(expandedRowAddresses, rowAddress)
          ? // remove rowAddress from expandedRowAddresses
            expandedRowAddresses.filter(a => a !== rowAddress)
          : // add rowAddress to expandedRowAddresses
            expandedRowAddresses.concat([rowAddress]),
      });
    }
  };

  setTableState = (
    stateUpdate: Pick<
      WBTableState,
      'columnDragAccessor' | 'columnDropAccessor' | 'columnResizeOffset'
    >
  ) => {
    this.setState(stateUpdate);
  };

  handleMouseMove = (e: MouseEvent) => {
    if (this.props.isResizingFullHeight) {
      const initialValues = this.state.dragInitialValues;
      const draggedWidth = this.getDraggedWidth();
      const addedWidth = draggedWidth - initialValues.maxPinnedColumnsWidth;

      const runNameColumnWidth =
        draggedWidth < initialValues.runNameColumnWidth
          ? draggedWidth
          : initialValues.runNameColumnWidth + addedWidth;

      this.setState({
        currentClientX: e.clientX,
        // When dragging, we move some column width state into the component to
        // improve render performance in large workspaces. Instead of rendering
        // the runs column from redux state, we get the width from local
        // component state, while the user is resizing that column. Once the
        // user releases the mouse button, the column width is propagated up to
        // redux.
        dragCurrentValues: {
          runNameColumnWidth,
          clientX: e.clientX,
        },
      });
    }
  };

  getDraggedWidth = () =>
    this.state.dragInitialValues.width +
    this.state.currentClientX -
    this.state.dragInitialValues.clientX;

  setColumnWidth = (accessor: string, value: number) => {
    const tableSettings = this.tableSettings();
    this.setTableSettings({
      ...tableSettings,
      columnWidths: {
        ...tableSettings.columnWidths,
        [accessor]: value,
      },
    });
  };

  computeDestinedIndex = () => {
    const draggedWidth = this.getDraggedWidth();
    let closestIndex = 0;
    this.snapToWidths.forEach((width, index) => {
      if (
        Math.abs(draggedWidth - width) <
        Math.abs(draggedWidth - this.snapToWidths[closestIndex])
      ) {
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  handleMouseUp = () => {
    if (this.props.isResizingFullHeight) {
      this.setTableSettings({
        ...this.tableSettings(),
        pinnedColumnsShown: this.computeDestinedIndex() + 1,
      });

      const initialWidth = this.state.dragInitialValues.width;
      if (this.currentWidth !== initialWidth) {
        // NOTE: This *will not* detect changes from resizing pinned column headers.
        // It only tracks if the resizer for the runs selector as a whole was used.
        Analytics.track('Runs selector resized', {
          initialWidth,
          updatedWidth: this.currentWidth,
        });
      }

      // User has stopped dragging, so propagate the runs list column width to
      // the redux state via the `setColumnWidth` method. Then reset the drag
      // current values. See handleMouseMove for why we do things this way.
      const runNameColumnWidth =
        this.state.dragCurrentValues?.runNameColumnWidth;
      if (runNameColumnWidth != null) {
        this.setColumnWidth('run:displayName', runNameColumnWidth);
      }
      this.props.setIsResizingFullHeight(false);
      this.setState({
        dragCurrentValues: null,
      });

      // trick panels into refreshing layout
      window.dispatchEvent(new Event('resize'));
    }
  };

  componentDidMount() {
    const scrollRef = this.scrollRef.current;
    if (scrollRef) {
      scrollRef.addEventListener('mousewheel', this.preventBackScroll);
    }

    // expandability
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('resize', this.handleResize);

    this.props.onSetCurrentPage?.(this.state.currentPage);
  }

  componentDidUpdate() {
    const isPageShownPastLastPage = isPastLastPage({
      currentPage: this.state.currentPage,
      pageSize: this.pageSize(),
      pagingMode: this.props.runsPagingMode,
      totalRowCount: this.props.totalRowCount,
    });
    if (!isPageShownPastLastPage) {
      return;
    }

    this.setCurrentPage(this.maxPage());
  }

  componentWillUnmount() {
    // expandability
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    // a paging mode change remounts the table, so the resize listener has to go with it
    window.removeEventListener('resize', this.handleResize);
    // Clear the shared resizing flag if we unmount mid-drag, otherwise the
    // next instance would start in a resize it never initiated.
    if (this.state.dragCurrentValues != null) {
      this.props.setIsResizingFullHeight(false);
    }

    if (this.scrollRef.current) {
      this.scrollRef.current.removeEventListener(
        'mousewheel',
        this.preventBackScroll
      );
    }
  }

  // This guys prevents the browser backwards behavior some OS Browser combos have for the table.
  preventBackScroll = (e: any) => {
    if (!this.scrollRef.current) {
      return;
    }

    const node = this.scrollRef.current;
    const maxX = node.scrollWidth - node.offsetWidth;

    // If node event looks like it will scroll beyond the bounds of the element, prevent it and set the scroll to the boundary manually
    if (
      // Must be scrollable
      node.offsetWidth !== node.scrollWidth &&
      // More horizontal movement than vertical; this is a hack that sort of works
      Math.abs(e.deltaX) >= Math.abs(e.deltaY) &&
      // Maxed out left side
      ((node.scrollLeft <= 0 && e.deltaX < 0) ||
        // Maxed out right side
        node.scrollLeft + node.offsetWidth >= node.scrollWidth)
    ) {
      e.preventDefault();
      // Manually set the scroll to the boundary
      node.scrollLeft = Math.max(0, Math.min(maxX, node.scrollLeft + e.deltaX));
      node.scrollTop = node.scrollTop + e.deltaY;
    }
  };

  setTableSettings = (newSettings: TableSettings) => {
    this.props.setTableSettings(newSettings);
  };

  moveColumn = () => {
    const fromAccessor = this.state.columnDragAccessor;
    const toAccessor = this.state.columnDropAccessor;
    if (fromAccessor && toAccessor && fromAccessor !== toAccessor) {
      this.setTableSettings(
        TableCols.moveBefore(this.tableSettings(), fromAccessor, toAccessor)
      );
      this.setState({
        columnDragAccessor: undefined,
        columnDropAccessor: undefined,
      });
    }
  };

  togglePinnedColumn = (columnAccessor: string): void => {
    this.setTableSettings(
      TableCols.togglePinned(this.tableSettings(), columnAccessor)
    );
  };

  hideColumn = (columnAccessor: string): void => {
    this.setTableSettings(
      TableCols.hideColumns(this.tableSettings(), [columnAccessor])
    );
  };

  addFilter = (
    key: RunTypes.Key,
    op: FilterTypes.ValueOp,
    value: RunTypes.Value
  ) => {
    const {setFilters} = this.props;
    if (setFilters == null) {
      return;
    }
    setFilters(
      addFilterFromWBTableToStored(this.props.filters, key, op, value)
    );
  };

  updateSort = (updateFn: (sort: QuerySort) => void) => {
    if (this.props.updateSort) {
      this.props.updateSort(updateFn);
    } else {
      // TODO: actually implement local sorting
      // this.setState(updateFn);
    }
    this.resetPagination();
  };

  setGrouping = (newGrouping: RunTypes.Key[]) => {
    this.props.setGrouping(newGrouping);
    this.resetPagination();
  };

  addGroup = (newGroup: RunTypes.Key) => {
    this.setGrouping([...this.grouping(), newGroup]);
  };

  toggleExpandedWithButton = () => {
    if (this.props.toggleExpandRunsTableWithTracking) {
      this.props.toggleExpandRunsTableWithTracking('button');
    }
  };

  onSliderMouseDown = (e: any) => {
    e.preventDefault();
    const runNameColumnWidth = getColumnWidth(this.tableSettings(), {
      section: 'run',
      name: 'displayName',
    });

    this.props.setIsResizingFullHeight(true);
    this.setState({
      currentClientX: e.clientX,
      dragInitialValues: {
        width: this.currentWidth,
        clientX: e.clientX,
        runNameColumnWidth,
        maxPinnedColumnsWidth:
          this.snapToWidths[this.snapToWidths.length - 1] +
          RUNS_SELECTOR_BORDER_WIDTH,
      },
      dragCurrentValues: {
        runNameColumnWidth,
        clientX: e.clientX,
      },
    });
  };

  updateScroll = () => {
    if (this.scrollRef.current == null) {
      return;
    }
    const x = this.scrollRef.current.scrollLeft;
    const y = this.scrollRef.current.scrollTop;
    const newScrolled = {
      scrolledHorizontally: x > 0,
      scrolledVertically: y > 0,
    };
    if (
      newScrolled.scrolledHorizontally !== this.state.scrolledHorizontally ||
      newScrolled.scrolledVertically !== this.state.scrolledVertically
    ) {
      this.setState(newScrolled);
    }
    this.updateScrollXBounds();
  };

  dualRef = (element: HTMLDivElement | null) => {
    this.props.onScreenRef(element);
    this.wrapperRef.current = element;
  };

  render() {
    const {
      className,
      tableLoadMore,
      totalRowCount,
      tableMessage,
      clearTableMessage,
      extraTableActionsLeft,
      extraTableActionsRight,
      extraTableActionsTop,
      fixedColumns,
      openSortPopup,
      onScreen,
      columnResizingAccessor,
    } = this.props;
    const {
      scrolledHorizontally,
      scrolledVertically,
      scrollXBounds,
      currentPage,
      childrenPaginationMap,
      expandedRowAddresses,
      columnDragAccessor,
      columnDropAccessor,
    } = this.state;

    // /* SORTING */
    // tableSettings() builds a new object on every call, so build it once and
    // reuse it for the rest of the render.
    const tableSettings = this.tableSettings();
    const visibleColumns = this.visibleColumnsInternal(
      tableSettings,
      this.props.columns
    );

    const pinnedColumns = this.pinnedColumns(tableSettings, visibleColumns);

    // fixed columns should always be pinned. if unpinned, it's because we
    // had a bug that allowed users to unpin fixed cols. automatically pin
    // those columns back & users won't be able to unpin anymore.
    fixedColumns?.forEach(fixedColumn => {
      const isPinned = tableSettings.columnPinned[fixedColumn];
      if (!isPinned) {
        this.togglePinnedColumn(fixedColumn);
      }
    });

    this.snapToWidths = [];
    let currentSnapToWidth = 0;
    pinnedColumns.forEach(col => {
      currentSnapToWidth += getColumnWidth(tableSettings, col.key);
      this.snapToWidths.push(currentSnapToWidth);
    });
    const pinnedColumnsShown = tableSettings.pinnedColumnsShown
      ? Math.min(tableSettings.pinnedColumnsShown, pinnedColumns.length)
      : 1;
    this.currentWidth =
      this.snapToWidths[pinnedColumnsShown - 1] +
      this.state.columnResizeOffset +
      RUNS_SELECTOR_BORDER_WIDTH;

    const {dragCurrentValues} = this.state;
    const displayedWidth =
      this.props.isResizingFullHeight && dragCurrentValues != null
        ? Math.max(
            this.state.dragInitialValues.width +
              dragCurrentValues.clientX -
              this.state.dragInitialValues.clientX,
            this.snapToWidths[0]
          )
        : this.currentWidth;

    const destinedIndex = this.computeDestinedIndex();
    const destinedWidth = this.snapToWidths[destinedIndex];

    const maxSidebarWidth =
      this.snapToWidths[this.snapToWidths.length - 1] +
      RUNS_SELECTOR_BORDER_WIDTH +
      this.state.columnResizeOffset;

    // The wrapper width determines how squashed everything to the right of the sidebar is
    const wrapperWidth = Math.min(displayedWidth, maxSidebarWidth);

    // The container width is the width of what you see as the sidebar
    // the closing ternary here is a hack fix for a regression caused by #10996
    // it restores dynamic resizing of the sidebar on mobile
    const containerWidth = this.props.expanded
      ? '100%'
      : this.props.isResizingFullHeight && destinedWidth > displayedWidth
        ? destinedWidth
        : window?.innerWidth && window?.innerWidth > 768 // HACK
          ? displayedWidth
          : '100%';

    const showBanner = this.props.renderBannerRow != null;

    const gridProps: WBTableGridContainerProps = {
      loading: this.props.loading,
      columns: visibleColumns,
      fixedColumns: fixedColumns || [],
      rows: this.rows(),
      runsPagingMode: this.props.runsPagingMode,
      pinnedRows: this.props.pinnedRows,
      showBannerRowSpacer: showBanner,
      addFilter: this.addFilter,
      updateSort: this.updateSort,
      updateTruncationType: this.props.updateTruncationType,
      addGroup: this.addGroup,
      expandedRowAddresses,
      toggleExpandedRow: this.toggleExpandedRow,
      tableSettings: this.tableSettings(),
      scrollXBounds,
      childrenPaginationMap,
      pinnedTreeRef: this.pinnedTreeRef,
      readOnly: this.props.disabledFeatures?.['edit-cols'],
      unpinnedTreeRef: this.props.expandable
        ? this.unpinnedTreeRef
        : this.scrollRef,
      expandable: this.props.expandable,
      expanded: this.props.expanded,
      toggleMinimizeRunSelectorWithTracking:
        this.props.toggleMinimizeRunSelectorWithTracking,
      isSingleMode: this.props.isSingleMode,
      isResizingFullHeight: this.props.isResizingFullHeight,
      showArtifactCounts: this.props.showArtifactCounts,
      showLogCounts: this.props.showLogCounts,
      topLevelQueryVariables: this.props.topLevelQueryVariables,
      useLeanGroupedRunsQuery: this.props.useLeanGroupedRunsQuery,
      setChildrenPagination: this.setChildrenPagination,
      setTableState: this.setTableState,
      setTableSettings: (settings: Partial<TableSettings>) => {
        this.setTableSettings({
          ...this.tableSettings(),
          ...settings,
        });
      },
      togglePinnedColumn: this.togglePinnedColumn,
      hideColumn: this.hideColumn,
      moveColumn: this.moveColumn,
      columnDragAccessor,
      columnDropAccessor,
      openSortPopup,
      truncationType: this.props.truncationType,
      sort: this.props.sort,
    };

    if (!onScreen) {
      return (
        <div
          ref={this.dualRef}
          style={{height: gridProps.rows.length * TABLE_ROW_HEIGHT + 138}}
        />
      );
    }

    const isTopActionsEmpty =
      this.props.disabledFeatures?.search &&
      this.props.disabledFeatures?.filter &&
      this.props.disabledFeatures?.group &&
      this.props.disabledFeatures?.sort &&
      this.props.disabledFeatures?.tag &&
      this.props.disabledFeatures?.move &&
      this.props.disabledFeatures?.delete &&
      this.props.disabledFeatures?.['create-sweep'] &&
      this.props.disabledFeatures?.export &&
      this.props.disabledFeatures?.['auto-cols'] &&
      this.props.disabledFeatures?.['edit-cols'];

    return (
      <div
        ref={this.dualRef}
        // Unmask the run selector in replay so support can see which runs/columns
        // were used — workspace run data, not credentials/account data.
        data-dd-privacy={DD_PRIVACY_ALLOW}
        className={clsx('wb-table-wrapper', className, {
          expanded: this.props.expanded,
          'scrolled-horizontally': scrolledHorizontally,
          'scrolled-vertically': scrolledVertically,
        })}
        style={
          {
            '--table-row-height': `${TABLE_ROW_HEIGHT}px`,
            ...(this.props.expandable
              ? {width: this.props.expanded ? '100%' : wrapperWidth}
              : {}),
          } as CSSProperties
        }>
        <div
          className={'wb-table-container'}
          ref={this.containerRef}
          style={
            this.props.expandable
              ? {
                  transitionDuration:
                    maxSidebarWidth < displayedWidth ||
                    columnResizingAccessor != null
                      ? '0s'
                      : undefined,
                  minWidth: containerWidth,
                  width: containerWidth,
                }
              : {}
          }>
          {/* TABLE ACTIONS */}
          <div
            className="wb-table-content"
            style={
              this.props.expandable
                ? {
                    WebkitMaskImage: this.props.isResizingFullHeight
                      ? destinedWidth < displayedWidth
                        ? `linear-gradient(to left, rgba(0,0,0,.5), rgba(0,0,0,.5) ${
                            displayedWidth - destinedWidth
                          }px, rgba(0,0,0,1) ${
                            displayedWidth - destinedWidth
                          }px)`
                        : `linear-gradient(to left, rgba(0,0,0,.5), rgba(0,0,0,.5) ${
                            destinedWidth - displayedWidth
                          }px, rgba(0,0,0,1) ${
                            destinedWidth - displayedWidth
                          }px)`
                      : undefined,
                  }
                : {}
            }>
            {!isTopActionsEmpty && (
              <div
                className="wb-table-top"
                data-dd-action-name="Run selector header"
                style={
                  this.props.isResizingFullHeight ? {width: destinedWidth} : {}
                }>
                {this.props.expandable && (
                  <div>
                    <div className="wb-table-title-wrapper">
                      <div className="wb-table-title-subwrapper">
                        <span className="wb-table-title">
                          {this.props.title}
                        </span>
                        <RunsCounter />
                      </div>
                      <WBTableTitleActions
                        disabledFeatures={this.props.disabledFeatures}
                        isTableExpanded={this.props.expanded}
                        toggleMinimize={
                          this.props.toggleMinimizeRunSelectorWithTracking
                        }
                        toggleExpanded={this.toggleExpandedWithButton}
                      />
                    </div>
                    {this.props.subTitle ? (
                      <div className="wb-table-subtitle">
                        Name: {this.props.subTitle}
                      </div>
                    ) : null}
                  </div>
                )}
                <WBTableActions
                  expandable={this.props.expandable}
                  expanded={this.props.expanded}
                  extraTableActionsLeft={extraTableActionsLeft}
                  extraTableActionsRight={extraTableActionsRight}
                  extraTableActionsTop={extraTableActionsTop}
                  fixedColumns={this.props.fixedColumns || []}
                  setTableSettings={this.props.setTableSettings}
                  tableSettings={this.tableSettings()}
                />
              </div>
            )}
            {/* TABLE MESSAGE */}
            <div className={`wb-table-message ${tableMessage ? 'active' : ''}`}>
              <div>{tableMessage}</div>
            </div>
            {/* Capture, not onScroll: when the table isn't expandable, the
                inner .wb-tree is the scroller (see unpinnedTreeRef), and
                scroll events don't bubble up to this wrapper. */}
            <div
              className="wb-table-grid-wrapper"
              onScrollCapture={this.updateScroll}
              ref={this.props.expandable ? this.scrollRef : undefined}>
              {showBanner ? (
                <div className="wb-table-grid-banner-row">
                  {this.props.renderBannerRow?.()}
                </div>
              ) : null}
              {/* DATA TABLE */}
              <WBTableGridContainer {...gridProps} />
            </div>
          </div>
          {/* PAGINATION */}
          <WBTablePaginationContainer floating={Boolean(this.props.expandable)}>
            <WBTablePagination
              loading={this.props.loading}
              currentPage={currentPage}
              maxPage={this.maxPage()}
              currentPageSize={this.tableSettings().pageSize}
              totalRowCount={totalRowCount}
              onClickFirstPage={
                this.props.runsPagingMode === 'offset'
                  ? this.resetPagination
                  : undefined
              }
              onClickPrevPage={() => {
                if (clearTableMessage) {
                  clearTableMessage();
                }
                this.setCurrentPage(currentPage - 1);
              }}
              onClickNextPage={() => {
                if (clearTableMessage) {
                  clearTableMessage();
                }
                const newPage = currentPage + 1;
                if (tableLoadMore != null) {
                  tableLoadMore(newPage);
                }
                this.setCurrentPage(newPage);
              }}
              setPageSize={newPageSize => {
                this.setTableSettings({
                  ...this.tableSettings(),
                  pageSize: newPageSize,
                });
                this.resetPagination();
              }}
            />
          </WBTablePaginationContainer>
          {this.props.expandable && !this.props.expanded && (
            <>
              <div
                className="wb-table-slider"
                onMouseDown={this.onSliderMouseDown}
              />
              {this.props.isResizingFullHeight && (
                <div className="dragging-overlay" />
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

function useWBTableProps() {
  const viewer = useViewer();
  const [onScreenRef, onScreen] = useInView({
    rootMargin: '500px',
    triggerOnce: true,
  });
  const {
    columnResizingAccessor,
    isResizingFullHeight,
    setIsResizingFullHeight,
  } = useRunSelectorResizingContext();
  const workspaceDrawerContext = useWorkspaceDrawerContextMaybe();
  const handleSetIsResizingFullHeight = useCallback(
    (value: boolean) => {
      setIsResizingFullHeight(value);
      // If run selector is resizing, close workspace drawer because this will affect
      // panelBankWidth. panelBankWidth is used to determine which drawer to show, inline or floating
      if (value && workspaceDrawerContext?.contentType != null) {
        workspaceDrawerContext?.closeWorkspaceDrawer(true); // skipping animation
      }
    },
    [setIsResizingFullHeight, workspaceDrawerContext]
  );
  return {
    viewer,
    onScreenRef,
    onScreen,
    columnResizingAccessor,
    isResizingFullHeight,
    setIsResizingFullHeight: handleSetIsResizingFullHeight,
  };
}

export const WBTable = (
  props: WBTableProps & {tableRef?: RefObject<WBTableComp | null>}
) => {
  const selectedProps = useWBTableProps();
  return (
    <WBTableComp
      // oxlint-disable-next-line react/refs
      ref={props.tableRef as RefObject<WBTableComp>}
      // oxlint-disable-next-line react/refs
      {...props}
      {...selectedProps}
    />
  );
};
